// src/context/ConversationsContext.jsx
// -----------------------------------------------------------------------------
// Contexte unifié pour toutes les conversations (Lux, DIP, Experts)
// Utilise des adaptateurs pour abstraire Voiceflow vs Dify
// -----------------------------------------------------------------------------
import { createContext, useReducer, useEffect, useState, useCallback, useMemo, useContext } from "react";
import { vfInteract, vfUpdateVariables, vfFetchState } from "../api/VoiceflowInteractions";
import { AuthContext } from "./AuthContext";
import { HistoryContext } from "./HistoryContext";
import { createConversation, addMessage, fetchConversations, rateConversationApi } from "../api/conversation";
import { sendDifyMessage } from "../api/dify";
import { agentConfig, getAgentType, getChatId, getXanoJobId } from "../config/agentConfig";

// =============================================================================
// CONTEXT
// =============================================================================
export const ConversationsContext = createContext({
  convs: {},
  customAction: {},
  sendToVF: () => Promise.resolve(),
  sendMessage: () => Promise.resolve(),
  initSession: async () => {},
  rateConversation: async () => {},
  resetCustomAction: () => {},
  resetSession: () => {},
  setWaitingForFeedback: () => {},
  askForFeedback: async () => {},
  waitingForFeedback: false
});

function reducerAction(state = {}, action) {
  const { type, payload, event } = action;
  switch (type) {
    case "ADD_ACTION":
      return { event, payload };
    case "RESET_CUSTOM_ACTION":
      return {};
    default:
      return state;
  }
}

function reducer(state = {}, action) {
  const { type, chatId, conversation, message, rating, difyConversationId } = action;
  switch (type) {
    case "ADD_MSG":
      return {
        ...state,
        [chatId]: {
          ...state[chatId],
          messages: [...(state[chatId]?.messages || []), message]
        },
      };
    case "INIT_SESSION":
      if (!chatId) return state;
      return state[chatId]
        ? state
        : {
            ...state,
            [chatId]: conversation
          };
    case "UPDATE_DIFY_CONVERSATION_ID":
      return {
        ...state,
        [chatId]: {
          ...state[chatId],
          dify_conversation_id: difyConversationId
        }
      };
    case "RATE_CONV":
      return {
        ...state,
        [chatId]: { ...state[chatId], rating },
      };
    case "RESET_SESSION":
      return {};
    default:
      return state;
  }
}

function setBotInfos(title, picture, name, gender, bio, suggestions) {
  return {
    name,
    job: title,
    gender,
    avatar: picture,
    bio: bio || '',
    suggestions: suggestions || []
  };
}

function initConvs() {
  try {
    const saved = localStorage.getItem("conversations");

    if (!saved) return {};

    const parsed = JSON.parse(saved);

    // Migration: vérifier si c'est l'ancien format (sans ciaov2_bot_id)
    // Si 'lux' existe mais n'a pas de ciaov2_bot_id, c'est l'ancien format
    if (parsed.lux && !parsed.lux.ciaov2_bot_id) {
      console.log('[Migration] Ancien format détecté, nettoyage du localStorage');
      localStorage.removeItem("conversations");
      return {};
    }

    return parsed;
  } catch {
    return {};
  }
}

export function ConversationsProvider({ children }) {
  const [convs, dispatch] = useReducer(reducer, {}, initConvs);
  const [customAction, dispatchAction] = useReducer(reducerAction, {});
  const [isWaitingForResponse, setIsWaitingForResponse] = useState(false);
  const [isWaitingForFeedback, setIsWaitingForFeedback] = useState(false);
  const { user } = useContext(AuthContext);
  const { addAction } = useContext(HistoryContext);

  // Chargement des conversations au login
  useEffect(() => {
    if (!user?.id) {
      resetSession();
      resetCustomAction();
      setIsWaitingForResponse(false);
      return;
    }

    (async () => {
      try {
        const conversations = await fetchConversations(user.authToken);

        // Si aucune conversation, initialiser Lux
        if (conversations.length === 0) {
          const values = { id: 'lux', name: "Lux", title: "Conseiller en orientation" };
          console.log('INIT LUX SESSION');
          initSession(null, values, process.env.REACT_APP_VF_LUX_API_KEY);
        }

        // Charger les conversations existantes
        conversations.forEach((conversation) => {
          let chatId;
          if (conversation.bot.name === 'Lux') {
            chatId = 'lux';
          } else if (conversation.bot.name === 'Dip') {
            chatId = 'dip';
          } else if (conversation.bot.name === 'Sup') {
            chatId = 'sup';
          } else {
            chatId = `${conversation.bot.job.id}/${conversation.bot.gender}`;
          }

          if (convs[chatId]) return;

          dispatch({
            type: "INIT_SESSION",
            chatId,
            conversation
          });
        });
      } catch (err) {
        console.error("initSession failed:", err);
      }
    })();
  }, [user?.id]);

  // Persistance localStorage
  useEffect(() => {
    localStorage.setItem("conversations", JSON.stringify(convs));
  }, [convs]);

  const resetSession = useCallback(() => {
    setIsWaitingForFeedback(false);
    dispatch({ type: "RESET_SESSION" });
  }, []);

  const resetCustomAction = useCallback(() => {
    dispatchAction({ type: "RESET_CUSTOM_ACTION" });
  }, []);

  /**
   * Initialise une session de conversation
   * Compatible avec l'ancien code (apiKey en 3ème param pour VF)
   */
  const initSession = useCallback(
    async (history, values, apiKey) => {
      const agentType = getAgentType(values.id);
      const chatId = getChatId(values.id, values.gender);

      if (convs[chatId]) return;

      const config = agentConfig[agentType];

      // Déterminer les botInfos
      let botInfos;
      if (convs[chatId]) {
        botInfos = config.getBotInfos(user);
      } else {
        botInfos = setBotInfos(values.title, values.picture, values.name, values.gender);
      }

      const botType = (chatId === 'lux' || chatId === 'dip' || chatId === 'dip') ? chatId : 'job_expert'
      const jobId = (chatId === 'lux' || chatId === 'dip' || chatId === 'sup') ? null : values.id

      const gender = values.gender || user?.gender;
      // Utiliser un identifiant stable pour LUX (basé sur user.id) pour persister les variables
      const sessionSlug = chatId === 'lux' ? `lux_${user.id}` : Math.random().toString(36).substring(2, 15);

      setIsWaitingForResponse(true);

      // Créer dans Xano
      const conversation = await createConversation(
        {
          gender,
          sessionSlug,
          botName: botInfos.name,
          jobId: jobId,
          userId: user.id,
          botType
        },
        user.authToken
      );

      // Créer dans le state
      dispatch({
        type: "INIT_SESSION",
        chatId,
        conversation
      });

      // Initialiser selon le type d'agent
      if (config.type === 'dify') {
        // Dify n'a pas besoin d'initialisation
        setIsWaitingForResponse(false);
      } else {
        // Voiceflow
        const effectiveApiKey = apiKey || config.apiKey;
        const initVariables = config.getInitVariables
          ? config.getInitVariables(user, history, values)
          : {};

        try {
          await vfUpdateVariables(sessionSlug, initVariables, effectiveApiKey);
          const responses = await vfInteract(sessionSlug, { type: 'launch' }, effectiveApiKey);
          await registerBotResponses(responses, chatId, conversation.id);
          vfFetchState(sessionSlug, effectiveApiKey);
        } catch (err) {
          console.error("VF init failed:", err);
        }

        setIsWaitingForResponse(false);
      }
    },
    [convs, user]
  );

  /**
   * Envoie un message - méthode unifiée
   */
  const sendMessage = useCallback(
    async (jobId, gender, userText, shouldSave = true) => {
      const agentType = getAgentType(jobId);
      const chatId = getChatId(jobId, gender);
      const conv = convs[chatId];

      if (!conv) return;

      const config = agentConfig[agentType];
      const messageContent = typeof userText === 'string' ? userText : userText.payload;

      setIsWaitingForResponse(true);

      // Enregistrer le message utilisateur
      if (shouldSave) {
        const userMsg = {
          from: "user",
          content: messageContent,
          referrer_message_id: null,
          type: "text"
        };

        const message = await addMessage(
          { conversation_id: conv['id'], message: userMsg },
          user.authToken
        );

        dispatch({ type: "ADD_MSG", chatId, conv, message });

      }

      try {
        if (config.type === 'dify') {
          // Appel Dify via Xano
          const response = await sendDifyMessage(messageContent, conv['id'], chatId, user.authToken);

          // Mettre à jour l'ID de conversation Dify
          if (response.conversation_id && response.conversation_id !== conv.difyConversationId) {
            dispatch({ type: "UPDATE_DIFY_CONVERSATION_ID", chatId, difyConversationId: response.conversation_id });
          }

          // Ajouter la réponse
          const botMsg = {
            from: "bot",
            content: response.answer,
            referrer_message_id: null,
            type: "text"
          };

          const message = await addMessage(
            { conversation_id: conv['id'], message: botMsg },
            user.authToken
          );

          dispatch({ type: "ADD_MSG", chatId, conv, message });
        } else {
          // Appel Voiceflow
          const effectiveApiKey = config.apiKey || process.env.REACT_APP_VF_API_KEY;
          const responses = await vfInteract(
            conv.sessionSlug,
            { type: "text", payload: messageContent },
            effectiveApiKey
          );

          await registerBotResponses(responses, chatId, conv['id']);
        }
      } catch (err) {
        console.error("sendMessage failed:", err);
      }

      setIsWaitingForResponse(false);
    },
    [convs, user]
  );

  /**
   * Alias pour compatibilité avec l'ancien code
   */
  const sendToVF = useCallback(
    async (jobId, gender, userText, apiKey, shouldSave = true) => {
      return sendMessage(jobId, gender, userText, shouldSave);
    },
    [sendMessage]
  );

  /**
   * Enregistre les réponses Voiceflow
   */
  async function registerBotResponses(responses, chatId, conversation_id) {
    let previousId = null;

    for (const b of responses) {
      if (b.type === "text") {
        previousId = b.time;
        let message = { from: "bot", content: b.payload.message, referrer_message_id: null, id: previousId, type: "text" };

        message = await addMessage(
          { conversation_id, message },
          user.authToken
        );

        dispatch({ type: "ADD_MSG", chatId, message, message });
      } else if (b.type === "choice" && b.payload.buttons?.length > 0) {
        let message = {
          from: "bot",
          content: b.payload.buttons,
          referrer_message_id: previousId,
          type: "button"
        };

        message = await addMessage(
          { conversation_id, message },
          user.authToken
        );
        dispatch({ type: "ADD_MSG", chatId, message, message });

      } else if (b.type === "speak-to-expert" || b.type === "speak-to-lux") {
        setIsWaitingForFeedback(true);
        dispatchAction({ type: "ADD_ACTION", event: 'redirect', payload: b.payload });
      }
    }
  }

  const rateConversation = useCallback(
    async (jobId, gender, rating) => {
      const chatId = getChatId(jobId, gender);

      dispatch({ type: "RATE_CONV", chatId, rating });

      await rateConversationApi(
        { jobId: getXanoJobId(jobId), gender, rating },
        user.authToken
      );
    },
    [user]
  );

  const setWaitingForFeedbackValues = (values) => {
    localStorage.setItem("feedback", JSON.stringify(values));
  };

  const askForFeedback = async () => {
    const feedback = JSON.parse(localStorage.getItem("feedback"));

    await addAction({ type: "LUX_ASK_FEEDBACK", values: feedback });
    sendMessage(
      'lux',
      user.gender,
      `Je viens de parler avec ${feedback.name} qui exerce le métier de ${feedback.title}. Demande moi comment s'est déroulé ma discussion. Tu peux personnaliser la réponse en fonction du métier de ${feedback.name}.`,
      false
    );
    localStorage.setItem("feedback", JSON.stringify({}));
    setIsWaitingForFeedback(false);
  };

  const value = useMemo(
    () => ({
      convs,
      customAction,
      initSession,
      sendToVF,
      sendMessage,
      rateConversation,
      resetCustomAction,
      resetSession,
      isWaitingForResponse,
      isWaitingForFeedback,
      setWaitingForFeedbackValues,
      askForFeedback
    }),
    [convs, customAction, initSession, sendToVF, sendMessage, rateConversation, resetCustomAction, resetSession, isWaitingForResponse, isWaitingForFeedback, askForFeedback, user]
  );

  return (
    <ConversationsContext.Provider value={value}>
      {children}
    </ConversationsContext.Provider>
  );
}
